"use client"

import { useEffect, useRef, useCallback } from "react"
import { postLocation } from "@/lib/staff_api"
import { ApiError } from "@new-cti/frontend-shared/auth/api-core"
import { logger } from "@/lib/logger"

// 位置情報送信間隔（ミリ秒）- 5秒
const LOCATION_SEND_INTERVAL = 5 * 1000
// 送信を間引く移動距離の閾値（メートル）。前回送信位置からこの距離未満なら送信しない（停車中の無駄送信を削減）
const MIN_MOVE_DISTANCE_M = 15
// 同じ場所でも最低限この間隔で送信して「生存（最新時刻）」を更新する（ミリ秒）- 30秒
// 配車システム側の「位置不明（?）」判定が60秒のため、その半分で更新し続けることで停車中でも誤判定を防ぐ
const HEARTBEAT_INTERVAL = 30 * 1000

type Coords = { latitude: number; longitude: number }

/**
 * 2地点間の距離（メートル）をHaversine式で概算する
 */
function distanceMeters(a: Coords, b: Coords): number {
  const R = 6371000 // 地球半径(m)
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLng = toRad(b.longitude - a.longitude)
  const lat1 = toRad(a.latitude)
  const lat2 = toRad(b.latitude)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

export interface LocationTrackerHandlers {
  /** 出勤直後に一度も位置を取得できなかった場合に呼ばれる（権限拒否・非セキュア接続等）。 */
  onError?: () => void
  /** 別端末が稼働端末になった（位置送信が409で拒否された）場合に呼ばれる。呼び出し側で退勤表示にする。 */
  onTakenOver?: () => void
}

/**
 * 位置情報をバックグラウンドで定期送信するhook
 * - ブラウザのGeolocation APIで現在位置を取得
 * - 5秒間隔でサーバーに送信（送信元 deviceId を付与）
 * - 前回送信位置からの移動が小さい場合は送信を間引く（停車中の無駄送信削減）
 * - ページが非表示の間は送信を停止
 * - サーバが409（この端末は稼働端末でない）を返したらトラッキングを停止し onTakenOver を呼ぶ
 *
 * 退勤(トラッキング停止通知)は呼び出し側が leave API で行う。このhookは enabled=false では送信を止めるのみ。
 *
 * @param enabled 出勤中（トラッキングON）の時のみ送信する。
 * @param deviceId 送信元端末ID。稼働端末排他(active_device_id)の照合に使う。
 * @param handlers onError / onTakenOver コールバック。
 */
export function useLocationTracker(enabled: boolean, deviceId: string, handlers?: LocationTrackerHandlers) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isTrackingRef = useRef(false)
  // 前回サーバへ送信した位置と時刻（間引き判定用）
  const lastSentRef = useRef<{ coords: Coords; at: number } | null>(null)
  // 多重送信防止（前回の送信/取得が終わる前に次が走らないようにする）
  const inFlightRef = useRef(false)
  // handlers を ref 経由で参照し、依存配列を安定させる
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  // deviceId も ref 経由で最新値を参照する
  const deviceIdRef = useRef(deviceId)
  deviceIdRef.current = deviceId
  // 出勤後に一度でも位置取得に成功したか（成功後の一時的な失敗で出勤を取り消さないため）
  const hasFixRef = useRef(false)
  // onError の多重発火を防ぐ
  const errorNotifiedRef = useRef(false)

  // 位置取得に失敗した旨を通知する。一度でも取得成功済み、または通知済みなら何もしない。
  const notifyError = useCallback(() => {
    if (hasFixRef.current || errorNotifiedRef.current) return
    errorNotifiedRef.current = true
    handlersRef.current?.onError?.()
  }, [])

  const stopTracking = useCallback(() => {
    isTrackingRef.current = false
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const sendLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      notifyError()
      return
    }
    if (inFlightRef.current) return
    inFlightRef.current = true

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // 位置取得に成功（この後で送信を間引いても「取得はできた」とみなす）
          hasFixRef.current = true
          const coords: Coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }

          // 間引き判定: 直近送信から十分動いていない & ハートビート間隔内ならスキップ
          const last = lastSentRef.current
          const now = Date.now()
          if (last) {
            const moved = distanceMeters(last.coords, coords)
            const elapsed = now - last.at
            if (moved < MIN_MOVE_DISTANCE_M && elapsed < HEARTBEAT_INTERVAL) {
              return
            }
          }

          await postLocation({ ...coords, deviceId: deviceIdRef.current })
          lastSentRef.current = { coords, at: now }
        } catch (err) {
          // 409（この端末は稼働端末でない=別端末が出勤した）ならトラッキングを止めて呼び出し側へ通知する。
          if (err instanceof ApiError && err.status === 409) {
            stopTracking()
            handlersRef.current?.onTakenOver?.()
            return
          }
          // その他の送信失敗は次回リトライで対応するためログのみ
          logger.error("位置情報の送信に失敗しました")
        } finally {
          inFlightRef.current = false
        }
      },
      () => {
        // 位置情報取得失敗（権限拒否・非セキュア接続等）。一度も取得できていなければ通知する。
        inFlightRef.current = false
        notifyError()
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        // 5秒間隔に合わせ、キャッシュは最大4秒まで許容（同一座標の連投を避けつつ取得負荷も抑える）
        maximumAge: 4000,
      }
    )
  }, [notifyError, stopTracking])

  const startTracking = useCallback(() => {
    if (isTrackingRef.current) return
    isTrackingRef.current = true

    // 再開時（退勤→再出勤・バックグラウンド復帰）は前回送信記録を捨てて必ず初回送信する。
    // これを残すと「直前に送ったばかり」と間引かれ、再出勤が最大30秒配車に反映されない。
    lastSentRef.current = null

    // 初回即送信
    sendLocation()

    // 定期送信
    intervalRef.current = setInterval(sendLocation, LOCATION_SEND_INTERVAL)
  }, [sendLocation])

  useEffect(() => {
    // OFF（退勤・稼働端末でない）の場合は送信しない。停止通知(退勤)は呼び出し側の leave API が担う。
    if (!enabled) {
      stopTracking()
      return
    }

    // 出勤開始時に状態をリセット（前回の成功フラグや失敗通知を持ち越さない）
    hasFixRef.current = false
    errorNotifiedRef.current = false

    // ON（出勤中）: ページ表示状態に応じてトラッキングを制御
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startTracking()
      } else {
        stopTracking()
      }
    }

    // 初回開始
    startTracking()

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      stopTracking()
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [enabled, startTracking, stopTracking])
}
